/**
 * ============================================================================
 * Inventory 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Inventory（库存批次）模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createInventoryApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createInventoryApi } from '@bayes-mes/shared/api/inventory/factory'
 * export const inventoryApi = createInventoryApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const result = await inventoryApi.getInventoryBatchList({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createInventoryApi } from '@bayes-mes/shared/api/inventory/factory'
 * export const inventoryApi = createInventoryApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/inventory/factory
 * @description Inventory 模块 API 工厂函数
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-18
 * @last-updated 2025-10-18
 * 
 * @change-log
 * - 2025-10-18: 初始创建，实现所有基础 CRUD 和业务方法
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  InventoryBatch,                    // 库存批次类型
  InventoryBatchDetail,              // 库存批次详情类型
  InventoryBatchQueryParams,          // 列表查询参数
  CreateInventoryBatchForm,           // 创建表单
  UpdateInventoryBatchForm,           // 更新表单
  InventoryOutboundForm,              // 出库表单
  InventoryLockForm,                  // 锁定表单
  InventoryUnlockForm,                // 解锁表单
  InventoryTransferForm,              // 调拨表单
  InventoryBatchImportForm,           // 批量导入表单
  InventoryBatchImportResult,         // 批量导入结果
  MaterialInventorySummary,           // 物料库存汇总
  MaterialInventorySummaryQueryParams,// 物料库存汇总查询参数
  MaterialInventoryListItem,          // 物料库存列表项
  MaterialInventoryListQueryParams,   // 物料库存列表查询参数
  InventoryStatistics,                // 库存统计
  ExpiringSoonQueryParams,            // 即将过期查询参数
  ExpiredQueryParams,                 // 已过期查询参数
  // 盘点相关
  StocktakingOrder,                   // 盘点单
  StocktakingItem,                    // 盘点明细
  StocktakingStatistics,              // 盘点统计
  StocktakingOrderQueryParams,        // 盘点单查询参数
  StocktakingItemQueryParams,         // 盘点明细查询参数
  CreateStocktakingOrderForm,         // 创建盘点单表单
  CancelStocktakingOrderForm,         // 取消盘点单表单
  SubmitCountForm,                    // 提交盘点表单
  SubmitCountByBatchForm,             // 按批次号提交盘点表单
  // 月度报表相关
  MonthlyReport,                      // 月度出入库报表
  MonthlyReportQueryParams,           // 月度报表查询参数
  // 预警相关
  WarningStatistics,                  // 预警统计
  WarningStatisticsQueryParams,       // 预警统计查询参数
  StagnantQueryParams,                // 呆滞查询参数
  // 待入库相关
  PendingInboundItem,                 // 待入库项
  PendingInboundCount,                // 待入库数量统计
  PendingInboundQueryParams,          // 待入库查询参数
  // 常用物料相关
  FrequentMaterial,                   // 常用物料项
  FrequentMaterialQueryParams,        // 常用物料查询参数
  // 物料保障工作台 P3
  MaterialAssuranceAction,
  MaterialAssuranceActionQueryParams,
  MaterialAssuranceBatchActionResponse,
  MaterialAssuranceNote,
  MaterialAssuranceNoteQueryParams,
  MaterialAssuranceSettings,
  MaterialAssuranceScanReport,
  MaterialAssuranceScanRun,
  MaterialAssuranceScanRunQueryParams,
  MaterialAssuranceSuggestionForm,
  MaterialAssuranceSuggestionResponse,
  MaterialAssuranceTodoResponse,
  MaterialAssuranceView,
  MaterialAssuranceWatch,
  MaterialAssuranceWorkloadQueryParams,
  MaterialAssuranceWorkloadResponse,
  MaterialAssuranceWorkbenchQueryParams,
  MaterialAssuranceWorkbenchResponse,
  BatchCreateMaterialAssuranceActionForm,
  CreateMaterialAssuranceActionForm,
  CreateMaterialAssuranceNoteForm,
  ExecuteMaterialAssuranceTransferForm,
  FinishMaterialAssuranceActionForm,
  RunMaterialAssuranceScanForm,
  SaveMaterialAssuranceViewForm,
  UpdateMaterialAssuranceNoteForm,
  UpdateMaterialAssuranceSettingsForm,
  WatchMaterialAssuranceForm,
  // 库存初始化相关
  InventoryInitBatch,                 // 初始化批次
  InventoryInitStatistics,            // 初始化统计
  InventoryInitBatchQueryParams,      // 初始化批次查询参数
  CreateInventoryInitBatchForm,       // 创建初始化批次表单
  UpdateInventoryInitBatchForm,       // 更新初始化批次表单
  AddInventoryInitItemForm,           // 添加初始化明细表单
  UpdateInventoryInitItemForm,        // 更新初始化明细表单
  ApproveTransactionForm,             // 审核事务表单
  InventoryTransaction,               // 出入库事务类型
  BatchHistoryResponse,               // 批次历史响应
  BatchHistoryQueryParams,            // 批次历史查询参数
  // 出入库日报相关
  DailyMovementSummaryItem,           // 出入库日报汇总行
  DailyMovementTrend,                 // 物料出入库走势
  DailyMovementSummaryQueryParams,    // 日报查询参数
  DailyMovementTrendQueryParams,      // 走势查询参数
  // 非批次管理物料简化出入库
  SimpleInboundForm,                  // 简化入库表单
  SimpleOutboundForm,                 // 简化出库表单
  NonBatchMaterialSummaryQueryParams, // 非批次物料库存汇总查询参数
  NonBatchMaterialSummary,            // 非批次物料库存汇总响应
  InventoryScanResolveRequest,        // 库存扫码解析请求
  InventoryScanResolveResponse,       // 库存扫码解析响应
  // 物料库存分布（扫码辅卡 + 分布页）
  MaterialDistributionSummary,        // 物料库存「概要」响应
  MaterialDistributionResponse,       // 物料库存「分布」详情响应
  MaterialDistributionSummaryQueryParams, // 概要查询参数
  MaterialDistributionQueryParams,    // 分布详情查询参数
  NonBatchDiagnosticsResponse,        // 非批次库存诊断响应
  RepairNonBatchDiagnosticRequest,    // 非批次库存修复请求
  RepairNonBatchDiagnosticResponse,   // 非批次库存修复响应
  // 库存调整、冻结/解冻、强制删除
  InventoryAdjustForm,                // 库存调整表单
  InventoryFreezeForm,                // 冻结表单
  InventoryUnfreezeForm,              // 解冻表单
  InventoryForceDeleteForm,           // 强制删除表单
  // 领料/借用统计相关
  PickingStatsByPerson,               // 按人员统计领料
  PickingStatsByMaterial,             // 按物料统计领料
  PickingTrend,                       // 领料趋势
  BorrowStatsByPerson,                // 按人员统计借用
  BorrowStatsByMaterial,              // 按物料统计借用
  BorrowTrend,                        // 借用趋势
} from '../../types/inventory'

// ===== 导入通用类型 =====
import type { PaginationResult } from '../../types/api'

// ===== 导入统一的请求客户端接口 =====
// ⚠️ 重要：所有工厂函数都从这里导入接口，不要重复定义！
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

// ===== 导出工厂函数 =====
/**
 * 创建 Inventory 模块的 API 方法集合
 * 
 * 这是一个工厂函数，它接收一个"请求客户端"作为参数，
 * 返回一个包含所有 Inventory 相关 API 方法的对象。
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有 Inventory 相关的请求方法
 * 
 * 为什么这样设计？
 * - 解耦：API 逻辑与具体的请求实现分离
 * - 复用：同一套 API 定义可以在 Web 和 App 端使用
 * - 测试：可以注入 Mock 客户端进行单元测试
 */
export function createInventoryApi(client: IRequestClient) {
  return {
    // ===== 基础 CRUD 操作 =====
    
    /**
     * 获取库存批次列表（分页）
     * 
     * 使用场景：
     * - 在库存管理页面的主列表中使用
     * - 支持分页、搜索、筛选功能
     * - 用户可以根据仓库、物料、质量状态等条件查询
     * 
     * @param params - 查询参数对象（可选）
     *   - factory_id?: number - 工厂ID
     *   - material_id?: number - 物料ID
     *   - warehouse_id?: number - 仓库ID
     *   - location_id?: number - 库位ID
     *   - quality_status?: string - 质量状态（QUALIFIED/PENDING/REJECTED/QUARANTINE）
     *   - batch_no?: string - 批次号（模糊搜索）
     *   - search?: string - 搜索关键字（物料编码、名称、批次号）
     *   - only_available?: boolean - 只查询有可用库存的批次
     *   - only_active?: boolean - 只查询正常状态的批次
     *   - expiring_soon?: boolean - 查询即将过期的批次
     *   - expiring_soon_days?: number - 即将过期天数（默认30天）
     *   - expired?: boolean - 查询已过期的批次
     *   - sort_field?: string - 排序字段（默认 inbound_date）
     *   - sort_order?: 'asc' | 'desc' - 排序方向（默认 asc）
     *   - per_page?: number - 每页数量，默认 20
     *   - page?: number - 页码，从 1 开始
     * 
     * @returns Promise<PaginationResult<InventoryBatch>> 分页结果对象
     *   返回的数据结构：
     *   {
     *     data: InventoryBatch[],  // 库存批次列表
     *     total: number,            // 总记录数
     *     current_page: number,     // 当前页码
     *     per_page: number,         // 每页数量
     *     last_page: number,        // 最后一页页码
     *     from: number,             // 当前页起始记录号
     *     to: number                // 当前页结束记录号
     *   }
     * 
     * 数据流向（完整过程）：
     * 1. 用户操作：用户在库存管理页面选择仓库、输入搜索关键词或点击"下一页"
     * 2. 前端调用：组件调用 inventoryApi.getInventoryBatchList({ warehouse_id: 1, page: 2 })
     * 3. 适配器处理：适配器将参数转换为 HTTP 请求
     * 4. 网络传输：发送到后端 GET /api/v1/inventory/batches?warehouse_id=1&page=2
     * 5. 后端处理：
     *    - 路由匹配到 InventoryController@index 方法
     *    - 验证查询参数
     *    - 查询数据库，应用筛选条件和分页
     *    - 通过 InventoryBatchResource 格式化数据（snake_case → camelCase）
     * 6. 响应返回：后端返回 JSON 格式的分页数据
     * 7. 适配器解包：适配器提取 response.data.data
     * 8. 前端接收：组件收到 PaginationResult<InventoryBatch> 类型的数据
     * 9. 页面更新：使用数据更新表格显示
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取第一页（使用默认参数）
     * const page1 = await inventoryApi.getInventoryBatchList()
     * 
     * // 示例2：按仓库筛选
     * const result = await inventoryApi.getInventoryBatchList({
     *   warehouse_id: 1,
     *   per_page: 20
     * })
     * 
     * // 示例3：搜索功能
     * const searchResult = await inventoryApi.getInventoryBatchList({
     *   search: '批次001'
     * })
     * 
     * // 示例4：查询即将过期的批次
     * const expiring = await inventoryApi.getInventoryBatchList({
     *   expiring_soon: true,
     *   expiring_soon_days: 30
     * })
     * 
     * // 示例5：只查询有可用库存的批次
     * const available = await inventoryApi.getInventoryBatchList({
     *   only_available: true,
     *   quality_status: 'QUALIFIED'
     * })
     * ```
     * 
     * 错误处理：
     * - 404：资源不存在（路由错误）
     * - 422：参数验证失败
     * - 500：服务器内部错误
     */
    getInventoryBatchList(params?: InventoryBatchQueryParams) {
      return client.get<PaginationResult<InventoryBatch>>(API_ROUTES.inventoryBatches.list, { params })
    },
    
    /**
     * 获取所有库存批次（不分页）
     * 
     * 使用场景：
     * - 在下拉选择器中使用
     * - 获取特定条件下的所有批次数据
     * - 导出功能前获取所有数据
     * 
     * @param params - 查询参数（与 getInventoryBatchList 相同，但不包含分页参数）
     * 
     * @returns Promise<InventoryBatch[]> 库存批次数组
     * 
     * 代码示例：
     * ```typescript
     * // 获取指定物料的所有批次
     * const batches = await inventoryApi.getAllInventoryBatches({
     *   material_id: 101,
     *   only_available: true
     * })
     * ```
     */
    getAllInventoryBatches(params?: InventoryBatchQueryParams) {
      return client.get<InventoryBatch[]>(API_ROUTES.inventoryBatches.all, { params })
    },
    
    /**
     * 获取库存批次详情
     * 
     * 使用场景：
     * - 在批次详情页面显示完整信息
     * - 编辑批次时获取当前数据
     * - 查看批次的详细资料
     * 
     * @param id - 批次的唯一标识符（主键）
     * 
     * @returns Promise<InventoryBatchDetail> 单个批次对象（包含关联信息）
     * 
     * 数据流向：
     * 1. 用户点击列表中的某一行
     * 2. 前端调用：inventoryApi.getInventoryBatchDetail(123)
     * 3. 发送请求：GET /api/v1/inventory/batches/123
     * 4. 后端处理：InventoryController@show 方法
     *    - 验证 ID 参数
     *    - 从数据库查询指定 ID 的记录
     *    - 加载关联数据（物料、仓库、库位等）
     *    - 通过 InventoryBatchResource 格式化数据
     * 5. 返回数据：包含关联信息的完整批次对象
     * 6. 前端显示：在详情页或编辑表单中展示数据
     * 
     * 代码示例：
     * ```typescript
     * const detail = await inventoryApi.getInventoryBatchDetail(123)
     * console.log(detail.batchNo)         // 批次号
     * console.log(detail.availableQuantity) // 可用数量
     * console.log(detail.material?.name)   // 物料名称（关联数据）
     * ```
     * 
     * 错误处理：
     * - 404：批次不存在
     * - 422：ID 格式错误
     */
    getInventoryBatchDetail(id: number) {
      return client.get<InventoryBatchDetail>(API_ROUTES.inventoryBatches.detail(id))
    },
    
    /**
     * 根据批次号查询
     * 
     * 使用场景：
     * - 扫描批次条码/二维码后查询批次信息
     * - 根据批次号快速定位批次
     * - 验证批次号是否存在
     * 
     * @param batchNo - 批次号（唯一）
     * 
     * @returns Promise<InventoryBatch> 批次信息
     * 
     * 代码示例：
     * ```typescript
     * // 扫描条码后查询
     * const batch = await inventoryApi.findByBatchNo('B20251018001')
     * console.log(batch.quantity)  // 库存数量
     * ```
     * 
     * 错误处理：
     * - 404：批次号不存在
     */
    findByBatchNo(batchNo: string) {
      return client.get<InventoryBatch>(API_ROUTES.inventoryBatches.findByBatchNo(batchNo))
    },
    
    /**
     * 创建库存批次（入库）
     * 
     * 使用场景：
     * - 采购入库：收到供应商物料后创建批次
     * - 生产入库：产品生产完成后创建批次
     * - 其他入库：退货入库、调拨入库等
     * 
     * @param data - 创建批次的表单数据
     *   必填字段：
     *   - factory_id: number - 工厂ID
     *   - factory_code: string - 工厂编码
     *   - material_id: number - 物料ID
     *   - material_code: string - 物料编码
     *   - material_name: string - 物料名称
     *   - material_type: 'RAW'|'SEMI'|'PRODUCT' - 物料类型
     *   - batch_no: string - 批次号（唯一）
     *   - warehouse_id: number - 仓库ID
     *   - warehouse_code: string - 仓库编码
     *   - quantity: number - 库存数量
     *   - unit: string - 单位
     *   - inbound_date: string - 入库日期
     * 
     * @returns Promise<InventoryBatch> 创建成功后的批次对象（包含生成的 ID）
     * 
     * 数据流向：
     * 1. 用户填写入库表单，点击"提交"按钮
     * 2. 前端验证：表单验证（必填项、格式等）
     * 3. 前端调用：inventoryApi.createInventoryBatch(formData)
     * 4. 发送请求：POST /api/v1/inventory/batches
     * 5. 后端处理：InventoryController@store 方法
     *    - 验证数据
     *    - 检查批次号唯一性
     *    - 插入数据库
     *    - 创建入库事务记录
     *    - 记录审计日志
     * 6. 前端处理：显示成功提示，跳转到列表页
     * 
     * 代码示例：
     * ```typescript
     * const newBatch: CreateInventoryBatchForm = {
     *   factory_id: 1,
     *   factory_code: 'F001',
     *   material_id: 101,
     *   material_code: 'M001',
     *   material_name: '钢板',
     *   material_type: 'RAW',
     *   batch_no: 'B20251018001',
     *   warehouse_id: 1,
     *   warehouse_code: 'WH001',
     *   quantity: 1000,
     *   unit: 'kg',
     *   inbound_date: '2025-10-18',
     *   quality_status: 'QUALIFIED',
     * }
     * 
     * const created = await inventoryApi.createInventoryBatch(newBatch)
     * console.log(created.id)  // 新创建的批次 ID
     * ```
     * 
     * 错误处理：
     * - 422：数据验证失败（批次号重复、必填项缺失等）
     * - 403：无权限创建
     * - 500：服务器错误
     */
    createInventoryBatch(data: CreateInventoryBatchForm) {
      return client.post<InventoryBatch>(API_ROUTES.inventoryBatches.batches, data)
    },
    
    /**
     * 更新库存批次
     * 
     * 使用场景：
     * - 修改批次的质量状态
     * - 调整库存数量
     * - 更新过期日期
     * - 修改单位成本
     * 
     * @param id - 要更新的批次的 ID
     * @param data - 更新的数据（只需要包含要修改的字段）
     *   可更新字段：
     *   - quantity?: number - 库存数量
     *   - locked_quantity?: number - 锁定数量
     *   - quality_status?: string - 质量状态
     *   - expiry_date?: string - 过期日期
     *   - unit_cost?: number - 单位成本
     *   - status?: number - 状态（1-正常、0-冻结）
     *   - remark?: string - 备注
     * 
     * @returns Promise<InventoryBatch> 更新后的完整批次对象
     * 
     * 代码示例：
     * ```typescript
     * // 修改质量状态
     * const updated = await inventoryApi.updateInventoryBatch(123, {
     *   quality_status: 'QUALIFIED'
     * })
     * 
     * // 调整单位成本
     * const updated2 = await inventoryApi.updateInventoryBatch(123, {
     *   unit_cost: 15.5
     * })
     * ```
     * 
     * 错误处理：
     * - 404：批次不存在
     * - 422：数据验证失败
     * - 403：无权限修改
     */
    updateInventoryBatch(id: number, data: UpdateInventoryBatchForm) {
      return client.put<InventoryBatch>(API_ROUTES.inventoryBatches.inventoryBatches(id), data)
    },
    
    /**
     * 删除库存批次
     * 
     * 使用场景：
     * - 删除错误录入的批次（仅限无库存的批次）
     * - 清理历史数据
     * 
     * @param id - 要删除的批次的 ID
     * 
     * @returns Promise<void> 删除成功不返回数据
     * 
     * 注意事项：
     * - 只能删除库存数量为 0 的批次
     * - 删除前会检查是否还有库存
     * 
     * 代码示例：
     * ```typescript
     * await inventoryApi.deleteInventoryBatch(123)
     * console.log('删除成功')
     * ```
     * 
     * 错误处理：
     * - 404：批次不存在
     * - 409：无法删除（还有库存）
     * - 403：无权限删除
     */
    deleteInventoryBatch(id: number) {
      return client.delete<void>(API_ROUTES.inventoryBatches.delete(id))
    },
    
    // ===== 库存业务操作 =====
    
    /**
     * 出库
     * 
     * 使用场景：
     * - 生产领料：从仓库领取原材料用于生产
     * - 销售出库：发货给客户
     * - 其他出库：报废出库、调拨出库等
     * 
     * @param data - 出库表单数据
     *   - batch_id: number - 批次ID
     *   - quantity: number - 出库数量
     *   - business_type: string - 业务类型（MATERIAL_OUT/SALE_OUT等）
     *   - source_doc_type?: string - 源单据类型
     *   - source_doc_no?: string - 源单据号
     *   - source_doc_id?: number - 源单据ID
     *   - remark?: string - 备注
     * 
     * @returns Promise<InventoryBatch> 更新后的批次信息
     * 
     * 业务逻辑：
     * - 检查可用数量是否足够
     * - 减少库存数量
     * - 创建出库事务记录
     * - 更新可用数量
     * 
     * 代码示例：
     * ```typescript
     * // 生产领料
     * const result = await inventoryApi.outboundInventory({
     *   batch_id: 123,
     *   quantity: 100,
     *   business_type: 'MATERIAL_OUT',
     *   source_doc_type: 'WORK_ORDER',
     *   source_doc_no: 'WO202510180001',
     *   remark: '生产领料'
     * })
     * 
     * console.log(result.availableQuantity)  // 更新后的可用数量
     * ```
     * 
     * 错误处理：
     * - 404：批次不存在
     * - 422：数据验证失败
     * - 409：可用库存不足
     */
    outboundInventory(data: InventoryOutboundForm) {
      return client.post<InventoryBatch>(API_ROUTES.inventoryBatches.outbound, data)
    },
    
    /**
     * 锁定库存
     * 
     * 使用场景：
     * - 订单创建后预留库存
     * - 生产计划下达后锁定原材料
     * - 防止超卖
     * 
     * @param data - 锁定表单数据
     *   - batch_id: number - 批次ID
     *   - quantity: number - 锁定数量
     * 
     * @returns Promise<InventoryBatch> 更新后的批次信息
     * 
     * 业务逻辑：
     * - 检查可用数量是否足够
     * - 增加锁定数量
     * - 减少可用数量
     * - 不减少总库存
     * 
     * 代码示例：
     * ```typescript
     * // 订单锁定库存
     * const result = await inventoryApi.lockInventory({
     *   batch_id: 123,
     *   quantity: 50
     * })
     * 
     * console.log(result.lockedQuantity)      // 锁定数量增加
     * console.log(result.availableQuantity)   // 可用数量减少
     * console.log(result.quantity)            // 总库存不变
     * ```
     * 
     * 错误处理：
     * - 404：批次不存在
     * - 409：可用库存不足，无法锁定
     */
    lockInventory(data: InventoryLockForm) {
      return client.post<InventoryBatch>(API_ROUTES.inventoryBatches.lock, data)
    },
    
    /**
     * 解锁库存
     * 
     * 使用场景：
     * - 订单取消后释放库存
     * - 生产计划取消后解锁原材料
     * - 手动解锁操作
     * 
     * @param data - 解锁表单数据
     *   - batch_id: number - 批次ID
     *   - quantity: number - 解锁数量
     * 
     * @returns Promise<InventoryBatch> 更新后的批次信息
     * 
     * 业务逻辑：
     * - 检查锁定数量是否足够
     * - 减少锁定数量
     * - 增加可用数量
     * - 不改变总库存
     * 
     * 代码示例：
     * ```typescript
     * // 订单取消，解锁库存
     * const result = await inventoryApi.unlockInventory({
     *   batch_id: 123,
     *   quantity: 50
     * })
     * ```
     * 
     * 错误处理：
     * - 404：批次不存在
     * - 409：锁定数量不足，无法解锁
     */
    unlockInventory(data: InventoryUnlockForm) {
      return client.post<InventoryBatch>(API_ROUTES.inventoryBatches.unlock, data)
    },
    
    /**
     * 库存调拨
     * 
     * 使用场景：
     * - 仓库间调拨：从A仓调到B仓
     * - 库位间调拨：同一仓库内换库位
     * - 跨工厂调拨：从工厂A调到工厂B
     * 
     * @param data - 调拨表单数据
     *   - source_batch_id: number - 源批次ID
     *   - quantity: number - 调拨数量
     *   - target_warehouse_id: number - 目标仓库ID
     *   - target_warehouse_code: string - 目标仓库编码
     *   - target_location_id?: number - 目标库位ID
     *   - target_location_code?: string - 目标库位编码
     *   - remark?: string - 备注
     * 
     * @returns Promise<InventoryBatch> 目标批次信息
     * 
     * 业务逻辑：
     * - 检查源批次可用数量
     * - 减少源批次库存
     * - 查找或创建目标批次
     * - 增加目标批次库存
     * - 创建调拨事务记录
     * 
     * 代码示例：
     * ```typescript
     * // 从仓库1调拨到仓库2
     * const result = await inventoryApi.transferInventory({
     *   source_batch_id: 123,
     *   quantity: 100,
     *   target_warehouse_id: 2,
     *   target_warehouse_code: 'WH002',
     *   remark: '仓库间调拨'
     * })
     * 
     * console.log(result.warehouseId)  // 目标仓库ID
     * console.log(result.quantity)     // 调拨后的数量
     * ```
     * 
     * 错误处理：
     * - 404：源批次不存在
     * - 422：数据验证失败
     * - 409：可用库存不足
     */
    transferInventory(data: InventoryTransferForm) {
      return client.post<InventoryBatch>(API_ROUTES.inventoryBatches.transfer, data)
    },
    
    // ===== 统计与查询 =====
    
    /**
     * 获取物料库存汇总
     * 
     * 使用场景：
     * - 查看某个物料的总库存
     * - 按仓库、质量状态查看库存分布
     * - 库存盘点时的汇总数据
     * 
     * @param params - 查询参数
     *   - material_id: number - 物料ID（必填）
     *   - warehouse_id?: number - 仓库ID
     *   - quality_status?: string - 质量状态
     * 
     * @returns Promise<MaterialInventorySummary> 物料库存汇总信息
     *   返回的数据结构：
     *   {
     *     material_id: number,           // 物料ID
     *     total_quantity: number,        // 总库存数量
     *     locked_quantity: number,       // 总锁定数量
     *     available_quantity: number,    // 总可用数量
     *     batch_count: number,           // 批次数量
     *     warehouse_count: number,       // 仓库数量
     *     by_warehouse: [...],           // 按仓库分组
     *     by_quality_status: [...]       // 按质量状态分组
     *   }
     * 
     * 代码示例：
     * ```typescript
     * // 查看物料101的库存汇总
     * const summary = await inventoryApi.getMaterialInventorySummary({
     *   material_id: 101
     * })
     * 
     * console.log(summary.total_quantity)     // 总库存
     * console.log(summary.available_quantity) // 可用库存
     * console.log(summary.by_warehouse)       // 各仓库库存
     * ```
     */
    getMaterialInventorySummary(params: MaterialInventorySummaryQueryParams) {
      return client.get<MaterialInventorySummary>(API_ROUTES.inventoryBatches.materialSummary, { params })
    },

    /**
     * 获取物料库存列表（按物料分组汇总）
     * 
     * 使用场景：
     * - 在物料库存页面显示物料级别的库存汇总列表
     * - 支持按物料类型、仓库筛选
     * - 支持搜索物料编码/名称
     * 
     * @param params - 查询参数
     *   - search?: string - 搜索关键字（物料编码、名称）
     *   - material_type?: string - 物料类型（RAW/SEMI/PRODUCT）
     *   - warehouse_id?: number - 仓库ID
     *   - only_available?: boolean - 只查询有可用库存
     *   - per_page?: number - 每页数量
     *   - page?: number - 页码
     * 
     * @returns Promise<PaginationResult<MaterialInventoryListItem>> 分页的物料库存列表
     * 
     * 代码示例：
     * ```typescript
     * // 获取原材料库存列表
     * const list = await inventoryApi.getMaterialInventoryList({
     *   material_type: 'RAW',
     *   only_available: true,
     *   per_page: 20
     * })
     * 
     * console.log(list.items)  // 物料库存汇总数组
     * ```
     */
    getMaterialInventoryList(params?: MaterialInventoryListQueryParams) {
      return client.get<PaginationResult<MaterialInventoryListItem>>(API_ROUTES.inventoryBatches.batchesMaterialInventoryList, { params })
    },

    // ===== 物料保障工作台 P3 =====

    /**
     * 获取物料保障工作台聚合数据
     *
     * 汇总安全库存、近期出库频次、待入库、销售需求占用、关注状态和处理任务。
     */
    getMaterialAssuranceWorkbench(params?: MaterialAssuranceWorkbenchQueryParams) {
      return client.get<MaterialAssuranceWorkbenchResponse>(API_ROUTES.materialAssurance.workbench, { params })
    },

    /**
     * 获取物料保障个人阈值和提醒设置
     */
    getMaterialAssuranceSettings() {
      return client.get<MaterialAssuranceSettings>(API_ROUTES.materialAssurance.settings)
    },

    /**
     * 更新物料保障个人阈值和提醒设置
     */
    updateMaterialAssuranceSettings(data: UpdateMaterialAssuranceSettingsForm) {
      return client.put<MaterialAssuranceSettings>(API_ROUTES.materialAssurance.settings, data)
    },

    /**
     * 获取我的关注物料
     */
    getMaterialAssuranceWatches() {
      return client.get<MaterialAssuranceWatch[]>(API_ROUTES.materialAssurance.watches)
    },

    /**
     * 关注物料并可订阅提醒
     */
    watchMaterialAssurance(data: WatchMaterialAssuranceForm) {
      return client.post<MaterialAssuranceWatch>(API_ROUTES.materialAssurance.watches, data)
    },

    /**
     * 取消关注物料
     */
    unwatchMaterialAssurance(materialId: number) {
      return client.delete<void>(API_ROUTES.materialAssurance.unwatch(materialId))
    },

    /**
     * 获取保存视图
     */
    getMaterialAssuranceViews() {
      return client.get<MaterialAssuranceView[]>(API_ROUTES.materialAssurance.views)
    },

    /**
     * 保存当前筛选视图
     */
    saveMaterialAssuranceView(data: SaveMaterialAssuranceViewForm) {
      return client.post<MaterialAssuranceView>(API_ROUTES.materialAssurance.views, data)
    },

    /**
     * 更新保存视图
     */
    updateMaterialAssuranceView(id: number, data: SaveMaterialAssuranceViewForm) {
      return client.put<MaterialAssuranceView>(API_ROUTES.materialAssurance.view(id), data)
    },

    /**
     * 删除保存视图
     */
    deleteMaterialAssuranceView(id: number) {
      return client.delete<void>(API_ROUTES.materialAssurance.view(id))
    },

    /**
     * 获取协同备注
     */
    getMaterialAssuranceNotes(params?: MaterialAssuranceNoteQueryParams) {
      return client.get<PaginationResult<MaterialAssuranceNote>>(API_ROUTES.materialAssurance.notes, { params })
    },

    /**
     * 新增协同备注
     */
    createMaterialAssuranceNote(data: CreateMaterialAssuranceNoteForm) {
      return client.post<MaterialAssuranceNote>(API_ROUTES.materialAssurance.notes, data)
    },

    /**
     * 更新协同备注
     */
    updateMaterialAssuranceNote(id: number, data: UpdateMaterialAssuranceNoteForm) {
      return client.put<MaterialAssuranceNote>(API_ROUTES.materialAssurance.note(id), data)
    },

    /**
     * 删除协同备注
     */
    deleteMaterialAssuranceNote(id: number) {
      return client.delete<void>(API_ROUTES.materialAssurance.note(id))
    },

    /**
     * 获取补货/调拨/跟进入库处理任务
     */
    getMaterialAssuranceActions(params?: MaterialAssuranceActionQueryParams) {
      return client.get<PaginationResult<MaterialAssuranceAction>>(API_ROUTES.materialAssurance.actions, { params })
    },

    /**
     * 获取我的物料保障待办和关注风险
     */
    getMaterialAssuranceTodos() {
      return client.get<MaterialAssuranceTodoResponse>(API_ROUTES.materialAssurance.todos)
    },

    /**
     * 手动运行物料保障提醒扫描
     */
    runMaterialAssuranceScan(data?: RunMaterialAssuranceScanForm) {
      return client.post<MaterialAssuranceScanReport>(API_ROUTES.materialAssurance.scan, data)
    },

    /**
     * 获取物料保障扫描历史
     */
    getMaterialAssuranceScanRuns(params?: MaterialAssuranceScanRunQueryParams) {
      return client.get<PaginationResult<MaterialAssuranceScanRun>>(API_ROUTES.materialAssurance.scanRuns, { params })
    },

    /**
     * 获取物料保障扫描详情
     */
    getMaterialAssuranceScanRun(id: number) {
      return client.get<MaterialAssuranceScanRun>(API_ROUTES.materialAssurance.scanRun(id))
    },

    /**
     * 获取负责人任务负载看板
     */
    getMaterialAssuranceWorkload(params?: MaterialAssuranceWorkloadQueryParams) {
      return client.get<MaterialAssuranceWorkloadResponse>(API_ROUTES.materialAssurance.workload, { params })
    },

    /**
     * 预生成补货/调拨/跟进处理建议
     */
    getMaterialAssuranceSuggestions(data: MaterialAssuranceSuggestionForm) {
      return client.post<MaterialAssuranceSuggestionResponse>(API_ROUTES.materialAssurance.suggestions, data)
    },

    /**
     * 创建补货/调拨/跟进入库处理任务
     */
    createMaterialAssuranceAction(data: CreateMaterialAssuranceActionForm) {
      return client.post<MaterialAssuranceAction>(API_ROUTES.materialAssurance.actions, data)
    },

    /**
     * 批量生成补货/调拨/跟进入库处理任务
     */
    batchCreateMaterialAssuranceActions(data: BatchCreateMaterialAssuranceActionForm) {
      return client.post<MaterialAssuranceBatchActionResponse>(API_ROUTES.materialAssurance.batchActions, data)
    },

    /**
     * 完成处理任务
     */
    completeMaterialAssuranceAction(id: number, data?: FinishMaterialAssuranceActionForm) {
      return client.post<MaterialAssuranceAction>(API_ROUTES.materialAssurance.completeAction(id), data)
    },

    /**
     * 取消处理任务
     */
    cancelMaterialAssuranceAction(id: number, data?: FinishMaterialAssuranceActionForm) {
      return client.post<MaterialAssuranceAction>(API_ROUTES.materialAssurance.cancelAction(id), data)
    },

    /**
     * 执行调拨任务
     */
    executeMaterialAssuranceTransfer(id: number, data?: ExecuteMaterialAssuranceTransferForm) {
      return client.post<MaterialAssuranceAction>(API_ROUTES.materialAssurance.executeTransferAction(id), data)
    },
    
    /**
     * 获取库存统计信息
     * 
     * 使用场景：
     * - 在仪表盘显示库存概况
     * - 查看全局库存统计
     * - 监控库存异常情况
     * 
     * @returns Promise<InventoryStatistics> 库存统计信息
     *   返回的数据结构：
     *   {
     *     totalBatches: number,        // 总批次数
     *     totalQuantity: number,       // 总库存数量
     *     lockedQuantity: number,      // 总锁定数量
     *     availableQuantity: number,   // 总可用数量
     *     warehousesCount: number,     // 仓库数量
     *     materialsCount: number,      // 物料数量
     *     expiringSoonCount: number,   // 即将过期批次数
     *     expiredCount: number         // 已过期批次数
     *   }
     * 
     * 代码示例：
     * ```typescript
     * const stats = await inventoryApi.getInventoryStatistics()
     * 
     * console.log(`总批次数：${stats.totalBatches}`)
     * console.log(`即将过期：${stats.expiringSoonCount}`)
     * console.log(`已过期：${stats.expiredCount}`)
     * ```
     */
    getInventoryStatistics() {
      return client.get<InventoryStatistics>(API_ROUTES.inventoryBatches.statistics)
    },
    
    /**
     * 获取即将过期的批次
     * 
     * 使用场景：
     * - 预警管理：显示即将过期的批次列表
     * - 先进先出：优先使用即将过期的批次
     * - 库存周转管理
     * 
     * @param params - 查询参数
     *   - days?: number - 天数（默认30天）
     *   - per_page?: number - 每页数量
     *   - page?: number - 页码
     * 
     * @returns Promise<PaginationResult<InventoryBatch>> 分页的批次列表
     * 
     * 代码示例：
     * ```typescript
     * // 查询30天内将过期的批次
     * const expiring = await inventoryApi.getExpiringSoonBatches({
     *   days: 30,
     *   per_page: 20
     * })
     * 
     * expiring.data.forEach(batch => {
     *   console.log(`${batch.batchNo} 将于 ${batch.expiryDate} 过期`)
     * })
     * ```
     */
    getExpiringSoonBatches(params?: ExpiringSoonQueryParams) {
      return client.get<PaginationResult<InventoryBatch>>(API_ROUTES.inventoryBatches.expiringSoon, { params })
    },
    
    /**
     * 获取已过期的批次
     * 
     * 使用场景：
     * - 查看需要处理的过期批次
     * - 库存清理管理
     * - 报废处理
     * 
     * @param params - 查询参数
     *   - per_page?: number - 每页数量
     *   - page?: number - 页码
     * 
     * @returns Promise<PaginationResult<InventoryBatch>> 分页的批次列表
     * 
     * 代码示例：
     * ```typescript
     * const expired = await inventoryApi.getExpiredBatches({
     *   per_page: 20
     * })
     * 
     * console.log(`共有 ${expired.total} 个批次已过期`)
     * ```
     */
    getExpiredBatches(params?: ExpiredQueryParams) {
      return client.get<PaginationResult<InventoryBatch>>(API_ROUTES.inventoryBatches.expired, { params })
    },
    
    // ===== 导入导出 =====
    
    /**
     * 批量导入库存批次
     * 
     * 使用场景：
     * - 系统初始化时导入期初库存
     * - 批量入库操作
     * - 盘点后调整库存
     * 
     * @param data - 批量导入数据
     *   - batches: InventoryBatchImportItem[] - 批次列表
     * 
     * @returns Promise<InventoryBatchImportResult> 导入结果
     *   返回的数据结构：
     *   {
     *     total_count: number,       // 总数量
     *     success_count: number,     // 成功数量
     *     failed_count: number,      // 失败数量
     *     success_items: [...],      // 成功项目列表
     *     failed_items: [...]        // 失败项目列表（含错误信息）
     *   }
     * 
     * 代码示例：
     * ```typescript
     * const importData: InventoryBatchImportForm = {
     *   batches: [
     *     {
     *       factory_id: 1,
     *       factory_code: 'F001',
     *       material_id: 101,
     *       material_code: 'M001',
     *       material_name: '钢板',
     *       batch_no: 'B001',
     *       warehouse_id: 1,
     *       warehouse_code: 'WH001',
     *       quantity: 1000,
     *       unit: 'kg'
     *     },
     *     // ... 更多批次
     *   ]
     * }
     * 
     * const result = await inventoryApi.importInventoryBatches(importData)
     * 
     * console.log(`成功导入 ${result.success_count} 条`)
     * console.log(`失败 ${result.failed_count} 条`)
     * 
     * // 显示失败的记录
     * result.failed_items.forEach(item => {
     *   console.log(`第 ${item.row} 行失败：${item.error}`)
     * })
     * ```
     * 
     * 错误处理：
     * - 422：数据验证失败
     * - 部分失败：返回结果中包含失败详情
     */
    importInventoryBatches(data: InventoryBatchImportForm) {
      return client.post<InventoryBatchImportResult>(API_ROUTES.inventoryBatches.import, data)
    },
    
    // ===== 库存事务相关 =====
    
    /**
     * 获取库存事务列表（分页）
     * 
     * 使用场景：
     * - 在出入库事务列表页面中使用
     * - 支持分页、搜索、筛选功能
     * - 用户可以根据事务类型、业务类型、物料、仓库等条件查询
     * 
     * @param params - 查询参数对象（可选）
     *   - factory_id?: number - 工厂ID
     *   - transaction_type?: string - 事务类型（INBOUND/OUTBOUND/TRANSFER/ADJUST/INVENTORY）
     *   - business_type?: string - 业务类型
     *   - material_id?: number - 物料ID
     *   - batch_no?: string - 批次号
     *   - warehouse_id?: number - 仓库ID
     *   - approval_status?: string - 审核状态（PENDING/APPROVED/REJECTED）
     *   - start_date?: string - 开始日期
     *   - end_date?: string - 结束日期
     *   - search?: string - 搜索关键字
     *   - per_page?: number - 每页数量，默认 20
     *   - page?: number - 页码，从 1 开始
     * 
     * @returns Promise<PaginationResult<any>> 分页结果对象
     *   返回的数据结构：
     *   {
     *     items: Transaction[],  // 事务列表
     *     pagination: {
     *       total: number,        // 总记录数
     *       perPage: number,      // 每页数量
     *       currentPage: number,  // 当前页码
     *       lastPage: number      // 最后一页
     *     }
     *   }
     * 
     * 代码示例：
     * ```typescript
     * const result = await inventoryApi.getTransactionList({
     *   transaction_type: 'INBOUND',
     *   page: 1,
     *   per_page: 20
     * })
     * 
     * console.log(result.items)  // 事务列表
     * console.log(result.pagination.total)  // 总记录数
     * ```
     */
    getTransactionList(params?: any) {
      return client.get<PaginationResult<any>>(API_ROUTES.inventoryTransactions.list, { params })
    },
    
    /**
     * 审核出入库事务
     * 
     * 使用场景：
     * - 审核待审核的出入库事务
     * - 通过或拒绝事务
     * 
     * @param id - 事务ID
     * @param data - 审核表单
     *   - approved: boolean - 是否通过
     *   - remark?: string - 备注
     * 
     * @returns Promise<InventoryTransaction> 更新后的事务
     */
    approveTransaction(id: number, data: ApproveTransactionForm) {
      return client.put<InventoryTransaction>(API_ROUTES.inventoryTransactions.approveApplication(id), data)
    },

    /**
     * 导出库存事务数据
     * 
     * 使用场景：
     * - 导出当前筛选条件下的所有事务数据
     * - 生成 Excel 报表
     * 
     * @param params - 查询参数（与 getTransactionList 相同）
     * 
     * @returns Promise<Blob> Excel 文件二进制数据
     * 
     * 代码示例：
     * ```typescript
     * const blob = await inventoryApi.exportTransactions({
     *   transaction_type: 'INBOUND',
     *   start_date: '2025-01-01',
     *   end_date: '2025-01-31'
     * })
     * 
     * // 创建下载链接
     * const url = window.URL.createObjectURL(blob)
     * const link = document.createElement('a')
     * link.href = url
     * link.download = 'transactions.xlsx'
     * link.click()
     * ```
     */
    exportTransactions(params?: any) {
      return client.post<Blob>(API_ROUTES.inventoryTransactions.export, params, {
        responseType: 'blob'
      })
    },

    /**
     * 获取批次出入库历史记录
     * 
     * 使用场景：
     * - 查看某个批次的所有出入库记录
     * - 追溯批次的库存变动历史
     * 
     * @param batchNo - 批次号
     * @param params - 查询参数
     *   - material_id?: number - 物料ID，非批次物料共用默认批号时用于精确限定
     * 
     * @returns Promise<BatchHistoryResponse> 批次历史响应
     * 
     * 代码示例：
     * ```typescript
     * const history = await inventoryApi.getBatchHistory('BATCH-2025-001')
     * console.log(history.transactions)  // 该批次的所有出入库记录
     * console.log(history.totalInbound)  // 入库合计
     * ```
     */
    getBatchHistory(batchNo: string, params?: BatchHistoryQueryParams) {
      return client.get<BatchHistoryResponse>(API_ROUTES.inventoryTransactions.batchHistory(batchNo), { params })
    },

    // ===== 盘点相关 =====

    /**
     * 创建盘点单
     * 
     * @param data 创建盘点单表单
     *   - warehouse_id: 仓库ID（必填）
     *   - type: 盘点类型 FULL/PARTIAL/SPOT（必填）
     *   - remark: 备注
     *   - material_codes: 物料编码列表（部分盘点时使用）
     *   - location_ids: 库位ID列表（部分盘点时使用）
     *   - material_type: 物料类型（部分盘点时使用）
     *   - sample_count: 抽样数量（抽盘时使用）
     */
    createStocktakingOrder(data: CreateStocktakingOrderForm) {
      return client.post<StocktakingOrder>(API_ROUTES.inventory.stocktaking, data)
    },

    /**
     * 获取盘点单列表
     */
    getStocktakingOrders(params?: StocktakingOrderQueryParams) {
      return client.get<PaginationResult<StocktakingOrder>>(API_ROUTES.inventory.stocktaking, { params })
    },

    /**
     * 获取我的盘点任务
     */
    getMyStocktakingTasks(params?: { per_page?: number; page?: number; status?: string }) {
      return client.get<PaginationResult<StocktakingOrder>>(API_ROUTES.inventory.stocktakingMyTasks, { params })
    },

    /**
     * 获取盘点单详情
     */
    getStocktakingOrderDetail(id: number) {
      return client.get<StocktakingOrder>(API_ROUTES.inventory.inventoryStocktaking(id))
    },

    /**
     * 获取盘点明细列表
     */
    getStocktakingItems(orderId: number, params?: StocktakingItemQueryParams) {
      return client.get<PaginationResult<StocktakingItem>>(API_ROUTES.inventory.stocktakingItems(orderId), { params })
    },

    /**
     * 获取盘点统计
     */
    getStocktakingStatistics(orderId: number) {
      return client.get<StocktakingStatistics>(API_ROUTES.inventory.stocktakingStatistics(orderId))
    },

    /**
     * 根据批次号查找盘点明细
     */
    findStocktakingItemByBatch(orderId: number, batchNo: string) {
      return client.get<StocktakingItem>(API_ROUTES.inventory.stocktakingFindByBatch(orderId), { params: { batch_no: batchNo } })
    },

    /**
     * 提交盘点结果（按明细ID）
     */
    submitStocktakingCount(orderId: number, itemId: number, data: SubmitCountForm) {
      return client.post<StocktakingItem>(API_ROUTES.inventory.count(orderId, itemId), data)
    },

    /**
     * 提交盘点结果（按批次号）
     */
    submitStocktakingCountByBatch(orderId: number, data: SubmitCountByBatchForm) {
      return client.post<StocktakingItem>(API_ROUTES.inventory.countByBatch(orderId), data)
    },

    /**
     * 完成盘点单
     */
    completeStocktakingOrder(orderId: number) {
      return client.put<StocktakingOrder>(API_ROUTES.inventory.update(orderId))
    },

    /**
     * 取消盘点单
     */
    cancelStocktakingOrder(orderId: number, data?: CancelStocktakingOrderForm) {
      return client.put<StocktakingOrder>(API_ROUTES.inventory.cancel(orderId), data)
    },

    // ===== 库存预警相关 =====

    /**
     * 获取预警统计
     */
    getWarningStatistics(params?: WarningStatisticsQueryParams) {
      return client.get<WarningStatistics>(API_ROUTES.inventory.warningStatistics, { params })
    },

    /**
     * 获取低库存批次列表
     */
    getLowStockBatches(params?: { per_page?: number; page?: number }) {
      return client.get<PaginationResult<InventoryBatch>>(API_ROUTES.inventory.warningLowStock, { params })
    },

    /**
     * 获取呆滞库存批次列表
     */
    getStagnantBatches(params?: StagnantQueryParams) {
      return client.get<PaginationResult<InventoryBatch>>(API_ROUTES.inventory.warningStagnant, { params })
    },

    // ===== 待入库相关 =====

    /**
     * 获取待入库列表
     * 
     * 使用场景：
     * - APP端待入库页面显示待处理的入库任务
     * - 汇总采购收货待入库和生产完成待入库
     * 
     * @param params - 查询参数
     *   - type?: 'all' | 'purchase' | 'production' - 类型筛选
     *   - per_page?: number - 每页数量
     *   - page?: number - 页码
     * 
     * @returns Promise<PaginationResult<PendingInboundItem>> 分页的待入库列表
     */
    getPendingInboundList(params?: PendingInboundQueryParams) {
      return client.get<PaginationResult<PendingInboundItem>>(API_ROUTES.inventoryBatches.batchesPendingInbound, { params })
    },

    /**
     * 获取待入库数量统计
     * 
     * 使用场景：
     * - APP库存首页显示待入库数量badge
     * - 快速了解待处理入库任务数
     * 
     * @returns Promise<PendingInboundCount> 待入库数量统计
     */
    getPendingInboundCount() {
      return client.get<PendingInboundCount>(API_ROUTES.inventoryBatches.batchesPendingInboundCount)
    },

    /**
     * 确认采购入库
     * 
     * 使用场景：
     * - APP端待入库页面确认采购入库
     * - 将采购收货记录转为库存批次
     * 
     * @param id - 采购收货记录ID
     * @param data - 入库参数
     *   - warehouse_id: number - 仓库ID
     *   - location_id?: number - 库位ID（可选）
     * 
     * @returns Promise<InventoryBatch> 创建的库存批次
     */
    confirmPurchaseInbound(id: number, data: { warehouse_id: number, location_id?: number }) {
      return client.post<InventoryBatch>(API_ROUTES.inventoryBatches.confirmPurchaseInbound(id), data)
    },

    // ===== 常用物料相关 =====

    /**
     * 获取常用物料列表
     * 
     * 使用场景：
     * - APP端常用物料页面
     * - 统计近30/60天出入库频繁的物料
     * 
     * @param params - 查询参数
     *   - days?: number - 统计天数（默认30天）
     *   - per_page?: number - 每页数量
     *   - page?: number - 页码
     * 
     * @returns Promise<PaginationResult<FrequentMaterial>> 分页的常用物料列表
     */
    getFrequentMaterials(params?: FrequentMaterialQueryParams) {
      return client.get<PaginationResult<FrequentMaterial>>(API_ROUTES.inventoryBatches.batchesFrequentMaterials, { params })
    },

    // ===== 库存初始化相关 =====

    /**
     * 获取初始化批次列表
     * 
     * 使用场景：
     * - APP端库存初始化页面
     * - 管理初始化批次
     * 
     * @param params - 查询参数
     * @returns Promise<PaginationResult<InventoryInitBatch>> 分页的初始化批次列表
     */
    getInitBatchList(params?: InventoryInitBatchQueryParams) {
      return client.get<PaginationResult<InventoryInitBatch>>(API_ROUTES.inventory.list, { params })
    },

    /**
     * 获取初始化批次统计
     * 
     * @returns Promise<InventoryInitStatistics> 初始化统计
     */
    getInitBatchStatistics() {
      return client.get<InventoryInitStatistics>(API_ROUTES.inventory.statistics)
    },

    /**
     * 创建初始化批次
     * 
     * @param data - 创建表单
     * @returns Promise<InventoryInitBatch> 创建的批次
     */
    createInitBatch(data: CreateInventoryInitBatchForm) {
      return client.post<InventoryInitBatch>(API_ROUTES.inventory.initBatches, data)
    },

    /**
     * 获取初始化批次详情
     * 
     * @param id - 批次ID
     * @returns Promise<InventoryInitBatch> 批次详情
     */
    getInitBatchDetail(id: number) {
      return client.get<InventoryInitBatch>(API_ROUTES.inventory.detail(id))
    },

    /**
     * 更新初始化批次
     * 
     * @param id - 批次ID
     * @param data - 更新表单
     * @returns Promise<InventoryInitBatch> 更新后的批次
     */
    updateInitBatch(id: number, data: UpdateInventoryInitBatchForm) {
      return client.put<InventoryInitBatch>(API_ROUTES.inventory.inventoryInitBatches(id), data)
    },

    /**
     * 删除初始化批次
     * 
     * @param id - 批次ID
     * @returns Promise<void>
     */
    deleteInitBatch(id: number) {
      return client.delete<void>(API_ROUTES.inventory.delete(id))
    },

    /**
     * 获取初始化批次明细列表
     * 
     * @param batchId - 批次ID
     * @param params - 分页参数
     * @returns Promise<PaginationResult<InventoryBatch>> 分页的明细列表
     */
    getInitBatchItems(batchId: number, params?: { per_page?: number, page?: number }) {
      return client.get<PaginationResult<InventoryBatch>>(API_ROUTES.inventory.addItem(batchId), { params })
    },

    /**
     * 添加初始化批次明细
     * 
     * @param batchId - 批次ID
     * @param items - 明细列表
     * @returns Promise<InventoryBatch[]> 创建的明细
     */
    addInitBatchItems(batchId: number, items: AddInventoryInitItemForm[]) {
      return client.post<InventoryBatch[]>(API_ROUTES.inventory.addItem(batchId), { items })
    },

    /**
     * 删除初始化批次明细
     *
     * @param batchId - 批次ID
     * @param itemId - 明细ID
     * @returns Promise<void>
     */
    deleteInitBatchItem(batchId: number, itemId: number) {
      return client.delete<void>(API_ROUTES.inventory.initBatchesItems(batchId, itemId))
    },

    /**
     * 更新初始化批次明细（草稿态原地更新）
     *
     * 编辑明细/非批次累加应使用本接口，不要用"删旧+加新"——
     * 软删行仍占用唯一索引（物料+批次号+库位+质量状态），重插同键会 1062。
     *
     * @param batchId - 批次ID
     * @param itemId - 明细ID
     * @param data - 更新表单
     * @returns Promise<InventoryBatch> 更新后的明细
     */
    updateInitBatchItem(batchId: number, itemId: number, data: UpdateInventoryInitItemForm) {
      return client.put<InventoryBatch>(API_ROUTES.inventory.initBatchesItems(batchId, itemId), data)
    },

    /**
     * 标记初始化批次明细为已打印
     *
     * App 打印成功后回写标签打印状态，使「已打印/待打印」跨会话保持一致；
     * 请求体 item_ids 只允许本批次明细，允许草稿与已提交批次（只读模式仍可补打）。
     *
     * @param batchId - 批次ID
     * @param itemIds - 明细ID列表
     * @returns Promise<InventoryBatch[]> 更新后的明细
     */
    markInitBatchItemsPrinted(batchId: number, itemIds: number[]) {
      return client.post<InventoryBatch[]>(API_ROUTES.inventory.initBatchesItemsPrint(batchId), { item_ids: itemIds })
    },

    /**
     * 提交初始化批次
     * 
     * @param id - 批次ID
     * @returns Promise<InventoryInitBatch> 提交后的批次
     */
    submitInitBatch(id: number) {
      return client.post<InventoryInitBatch>(API_ROUTES.inventory.submit(id))
    },

    /**
     * 获取月度出入库报表
     * 
     * 使用场景：
     * - 查看指定仓库某个月的物料出入库统计
     * - 按物料分类筛选报表数据
     * - 支持按件/按包两种显示模式
     * - 查看每日详细的出入库数据
     * 
     * @param params - 查询参数
     *   - warehouse_id?: number - 仓库ID（可选，不填则查询所有仓库）
     *   - year_month?: string - 年月（格式：2025-12，默认当前月）
     *   - category?: string - 物料分类（如：原材料、半成品、成品等）
     *   - display_mode?: 'piece' | 'package' - 显示模式（默认piece）
     * 
     * @returns Promise<MonthlyReport> 月度报表数据
     *   - yearMonth: string - 年月
     *   - warehouseId?: number - 仓库ID
     *   - displayMode: 'piece' | 'package' - 显示模式
     *   - daysInMonth: number - 本月天数
     *   - totalMaterials: number - 物料总数
     *   - items: MonthlyReportItem[] - 报表数据项列表
     * 
     * 业务逻辑：
     * - 计算月初库存（上月最后一天的库存）
     * - 统计本月入库总量和出库总量
     * - 计算剩余库存 = 月初库存 + 入库 - 出库
     * - 计算日均消耗 = 出库总量 / 本月天数
     * - 获取每日的出入库明细数据
     * - 根据display_mode转换为件数或包数
     * 
     * 代码示例：
     * ```typescript
     * // 查看成品仓2025年12月的出入库报表（按件显示）
     * const report = await inventoryApi.getMonthlyReport({
     *   warehouse_id: 1,
     *   year_month: '2025-12',
     *   display_mode: 'piece'
     * })
     * 
     * console.log(report.totalMaterials) // 物料种类数
     * report.items.forEach(item => {
     *   console.log(item.materialName)      // 物料名称
     *   console.log(item.beginningStockPiece) // 月初库存（件）
     *   console.log(item.inboundPiece)      // 入库数量（件）
     *   console.log(item.outboundPiece)     // 出库数量（件）
     *   console.log(item.remainingPiece)    // 剩余库存（件）
     *   console.log(item.dailyData)         // 每日出入库数据
     * })
     * 
     * // 查看原材料分类的报表（按包显示）
     * const rawMaterialReport = await inventoryApi.getMonthlyReport({
     *   warehouse_id: 1,
     *   year_month: '2025-12',
     *   category: '原材料',
     *   display_mode: 'package'
     * })
     * ```
     * 
     * 响应数据结构：
     * ```typescript
     * {
     *   yearMonth: "2025-12",
     *   warehouseId: 1,
     *   displayMode: "piece",
     *   daysInMonth: 31,
     *   totalMaterials: 10,
     *   items: [
     *     {
     *       materialId: 1,
     *       materialCode: "M001",
     *       materialName: "青云抱莲桂林10g",
     *       specification: "10g*10包",
     *       unit: "kg",
     *       packageQuantity: 100,
     *       beginningStockPiece: 2000,
     *       beginningStockPackage: 20,
     *       inboundPiece: 500,
     *       inboundPackage: 5,
     *       outboundPiece: 800,
     *       outboundPackage: 8,
     *       remainingPiece: 1700,
     *       remainingPackage: 17,
     *       dailyConsumption: 25.81,
     *       dailyConsumptionRatio: "0.00/0.00",
     *       dailyData: {
     *         1: { date: "2025-12-01", inbound: 0, outbound: 100 },
     *         2: { date: "2025-12-02", inbound: 200, outbound: 50 },
     *         ...
     *       }
     *     }
     *   ]
     * }
     * ```
     * 
     * 错误处理：
     * - 400：参数错误（年月格式不正确）
     * - 404：仓库不存在
     * - 500：服务器错误
     */
    getMonthlyReport(params: MonthlyReportQueryParams) {
      return client.get<MonthlyReport>(API_ROUTES.inventoryTransactions.transactionsMonthlyReport, { params })
    },

    // ===== 出入库日报 =====

    /**
     * 获取出入库日报汇总（分页）
     *
     * 区间内发生过出入库动作的物料，按出入库合计降序分页；
     * 入/出口径与月度报表一致，closingStock 为查询期末结存
     * （查当日即当前实时库存）。
     *
     * @param params - 查询参数
     *   - start_date?: string - 起始日期（YYYY-MM-DD，默认今天）
     *   - end_date?: string - 结束日期（YYYY-MM-DD，默认今天）
     *   - page?: number - 页码
     *   - per_page?: number - 每页数量（默认20，上限100）
     *
     * @example
     * ```typescript
     * const result = await inventoryApi.getDailyMovementSummary({
     *   start_date: '2026-09-25',
     *   end_date: '2026-09-25',
     *   page: 1,
     *   per_page: 20
     * })
     * console.log(result.pagination.total)
     * result.items.forEach(item => console.log(item.materialName, item.closingStock))
     * ```
     */
    getDailyMovementSummary(params: DailyMovementSummaryQueryParams) {
      return client.get<PaginationResult<DailyMovementSummaryItem>>(
        API_ROUTES.inventoryTransactions.dailySummary,
        { params }
      )
    },

    /**
     * 获取物料出入库走势（按日）
     *
     * 返回区间内逐日的入库、出库与每日库存余额（账面结存）。
     *
     * @param params - 查询参数
     *   - material_id: number - 物料ID（必填）
     *   - start_date: string - 起始日期（YYYY-MM-DD）
     *   - end_date: string - 结束日期（YYYY-MM-DD）
     *
     * @example
     * ```typescript
     * const trend = await inventoryApi.getDailyMovementTrend({
     *   material_id: 1,
     *   start_date: '2026-08-26',
     *   end_date: '2026-09-25'
     * })
     * trend.days.forEach(d => console.log(d.date, d.inbound, d.outbound, d.closingBalance))
     * ```
     */
    getDailyMovementTrend(params: DailyMovementTrendQueryParams) {
      return client.get<DailyMovementTrend>(API_ROUTES.inventoryTransactions.dailyTrend, { params })
    },

    // ===== 非批次管理物料简化出入库 =====

    /**
     * 简化入库（非批次管理物料）
     * 
     * 使用场景：
     * - 非批次管理物料的快速入库操作
     * - 辅料、低值易耗品等不需要批次追溯的物料入库
     * - 重复入库会累加到同一个 DEFAULT 批次
     * 
     * @param data - 入库表单数据
     *   - material_id: number - 物料ID（必填）
     *   - warehouse_id: number - 仓库ID（必填）
     *   - quantity: number - 入库数量（必填，最小0.001）
     *   - location_id?: number - 库位ID（可选）
     *   - location_code?: string - 库位编码（可选）
     *   - remark?: string - 备注（可选）
     * 
     * @returns Promise<InventoryBatch> 更新后的库存批次信息
     * 
     * 业务逻辑：
     * - 检查物料是否为非批次管理物料（enable_batch = false）
     * - 查找该物料在指定仓库的 DEFAULT 批次
     * - 如果存在则累加数量，不存在则创建新的 DEFAULT 批次
     * - 创建入库事务记录（保证完整追溯）
     * 
     * 代码示例：
     * ```typescript
     * // 辅料入库
     * const batch = await inventoryApi.simpleInbound({
     *   material_id: 101,
     *   warehouse_id: 1,
     *   quantity: 100,
     *   remark: '辅料补货'
     * })
     * 
     * console.log(batch.quantity)  // 累加后的总数量
     * console.log(batch.batchNo)   // 'DEFAULT'
     * ```
     * 
     * 错误处理：
     * - 400：物料启用了批次管理，请使用标准入库功能
     * - 404：物料不存在 / 仓库不存在
     * - 422：数据验证失败
     */
    simpleInbound(data: SimpleInboundForm) {
      return client.post<InventoryBatch>(API_ROUTES.inventoryBatches.simpleInbound, data)
    },

    /**
     * 简化出库（非批次管理物料）
     * 
     * 使用场景：
     * - 非批次管理物料的快速出库操作
     * - 辅料、低值易耗品等不需要批次追溯的物料出库
     * - 自动从 DEFAULT 批次扣减库存
     * 
     * @param data - 出库表单数据
     *   - material_id: number - 物料ID（必填）
     *   - warehouse_id: number - 仓库ID（必填）
     *   - quantity: number - 出库数量（必填，最小0.001）
     *   - business_type: string - 业务类型（必填）
     *   - remark?: string - 备注（可选）
     * 
     * @returns Promise<InventoryBatch> 更新后的库存批次信息
     * 
     * 业务逻辑：
     * - 检查物料是否为非批次管理物料（enable_batch = false）
     * - 查找该物料在指定仓库的 DEFAULT 批次
     * - 检查可用数量是否足够
     * - 扣减库存并创建出库事务记录
     * 
     * 代码示例：
     * ```typescript
     * // 辅料领用
     * const batch = await inventoryApi.simpleOutbound({
     *   material_id: 101,
     *   warehouse_id: 1,
     *   quantity: 50,
     *   business_type: 'MATERIAL_OUT',
     *   remark: '生产领用'
     * })
     * 
     * console.log(batch.availableQuantity)  // 扣减后的可用数量
     * ```
     * 
     * 错误处理：
     * - 400：物料启用了批次管理，请使用标准出库功能
     * - 400：可用库存不足
     * - 404：物料不存在 / 仓库不存在 / 该物料在此仓库无库存
     * - 422：数据验证失败
     */
    simpleOutbound(data: SimpleOutboundForm) {
      return client.post<InventoryBatch>(API_ROUTES.inventoryBatches.simpleOutbound, data)
    },

    /**
     * 获取非批次管理物料的库存汇总
     * 
     * 使用场景：
     * - 查看非批次管理物料在各仓库的库存分布
     * - 快速了解某个辅料的总库存
     * 
     * @param params - 查询参数
     *   - material_id: number - 物料ID（必填）
     *   - warehouse_id?: number - 仓库ID（可选，不填则查询所有仓库）
     * 
     * @returns Promise<NonBatchMaterialSummary> 库存汇总信息
     *   - material_id: number - 物料ID
     *   - total_quantity: number - 总库存数量
     *   - available_quantity: number - 总可用数量
     *   - warehouse_count: number - 仓库数量
     *   - by_warehouse: Array<{ warehouse_id, warehouse_code, quantity, available_quantity }>
     * 
     * 代码示例：
     * ```typescript
     * // 查看物料在所有仓库的库存
     * const summary = await inventoryApi.getNonBatchMaterialSummary({
     *   material_id: 101
     * })
     * 
     * console.log(summary.total_quantity)  // 总库存
     * console.log(summary.by_warehouse)    // 各仓库库存明细
     * 
     * // 查看物料在指定仓库的库存
     * const warehouseSummary = await inventoryApi.getNonBatchMaterialSummary({
     *   material_id: 101,
     *   warehouse_id: 1
     * })
     * ```
     */
    getNonBatchMaterialSummary(params: NonBatchMaterialSummaryQueryParams) {
      return client.get<NonBatchMaterialSummary>(API_ROUTES.inventoryBatches.nonBatchSummary, { params })
    },

    /**
     * 解析扫码内容对应的库存语义。
     */
    scanResolve(data: InventoryScanResolveRequest) {
      return client.post<InventoryScanResolveResponse>(API_ROUTES.inventoryBatches.scanResolve, data)
    },

    /**
     * 获取物料库存「概要」（扫码辅卡 / 列表悬浮等场景）
     *
     * 返回内容：
     * - 全部库存数量（总量、可用、锁定）
     * - 维度计数（批次数、仓库数、库位数）
     * - 质量状态分布
     * - FIFO 提示（最早入库的批次，已合并多库位）
     * - 过期/即将过期预警（仅当物料配置 shelfLifeDays 时）
     * - 安全库存预警（min_stock 对比）
     *
     * @param materialId 物料ID
     * @param params 可选查询参数（warehouse_id 限定仓库）
     * @returns Promise<MaterialDistributionSummary>
     */
    getMaterialDistributionSummary(
      materialId: string | number,
      params?: MaterialDistributionSummaryQueryParams,
    ) {
      return client.get<MaterialDistributionSummary>(
        API_ROUTES.inventoryBatches.materialDistributionSummary(materialId),
        { params },
      )
    },

    /**
     * 获取物料库存「分布」详情（分布页用）
     *
     * 返回三种视图：
     * - summary: 汇总（同 distribution-summary）
     * - byZone: 按仓库 + 分区折叠分组（zone 参数可 lazy 加载单一分区）
     * - byBatch: 按批次合并（同 batch_no 跨库位合并）
     *
     * @param materialId 物料ID
     * @param params 可选查询参数（warehouse_id / zone / only_available）
     * @returns Promise<MaterialDistributionResponse>
     */
    getMaterialDistribution(
      materialId: string | number,
      params?: MaterialDistributionQueryParams,
    ) {
      // Laravel `boolean` 校验只接受 1/0/"1"/"0"/true/false，
      // 不接受 "true"/"false" 字符串。query string 默认会序列化 boolean
      // 为 "true"/"false"，所以这里手动转 1/0 保证后端校验通过。
      const normalizedParams: Record<string, any> = { ...params }
      if (normalizedParams.only_available !== undefined)
        normalizedParams.only_available = normalizedParams.only_available ? 1 : 0

      return client.get<MaterialDistributionResponse>(
        API_ROUTES.inventoryBatches.materialDistribution(materialId),
        { params: normalizedParams },
      )
    },

    /**
     * 获取非批次库存数据诊断结果。
     */
    getNonBatchDiagnostics() {
      return client.get<NonBatchDiagnosticsResponse>(API_ROUTES.inventoryBatches.nonBatchDiagnostics)
    },

    /**
     * 修复满足前置条件的非批次库存诊断项。
     */
    repairNonBatchDiagnostic(data: RepairNonBatchDiagnosticRequest) {
      return client.post<RepairNonBatchDiagnosticResponse>(API_ROUTES.inventoryBatches.repairNonBatchDiagnostic, data)
    },

    // ===== 库存调整、冻结/解冻、强制删除 =====

    /**
     * 库存调整（修正错误录入）
     * 
     * 使用场景：
     * - 发现库存数量录入错误时，直接调整为正确数量
     * - 盘点发现差异后，手动调整库存
     * - 系统自动生成调整事务记录，保留审计追踪
     * 
     * @param id - 批次ID
     * @param data - 调整数据
     *   - quantity: number - 调整后的数量（必填）
     *   - reason: string - 调整原因（必填）
     * 
     * @returns Promise<InventoryBatch> 更新后的批次信息
     * 
     * 业务规则：
     * - 调整后数量不能小于锁定数量
     * - 系统自动生成 ADJUST 类型的入库/出库事务
     * - 所有调整都会记录审计日志
     * 
     * 权限要求：inventory:inventory:adjust
     * 
     * 代码示例：
     * ```typescript
     * const batch = await inventoryApi.adjustInventory(123, {
     *   quantity: 100,
     *   reason: '录入错误，实际数量为100'
     * })
     * ```
     */
    adjustInventory(id: number, data: InventoryAdjustForm) {
      return client.post<InventoryBatch>(API_ROUTES.inventoryBatches.adjust(id), data)
    },

    /**
     * 冻结批次库存
     * 
     * 使用场景：
     * - 质量问题需要隔离库存
     * - 等待检验结果期间暂时冻结
     * - 防止问题批次被误用
     * 
     * @param id - 批次ID
     * @param data - 冻结数据
     *   - reason: string - 冻结原因（必填）
     * 
     * @returns Promise<InventoryBatch> 更新后的批次信息
     * 
     * 业务规则：
     * - 冻结后的库存无法出库
     * - 冻结操作会发送通知给相关人员
     * - 冻结记录保存在批次的 remark 字段
     * 
     * 权限要求：inventory:inventory:freeze
     * 
     * 代码示例：
     * ```typescript
     * const batch = await inventoryApi.freezeInventory(123, {
     *   reason: '质量问题，等待检验'
     * })
     * ```
     */
    freezeInventory(id: number, data: InventoryFreezeForm) {
      return client.post<InventoryBatch>(API_ROUTES.inventoryBatches.freeze(id), data)
    },

    /**
     * 解冻批次库存
     * 
     * 使用场景：
     * - 检验合格后解冻库存
     * - 问题已解决，恢复正常出库
     * 
     * @param id - 批次ID
     * @param data - 解冻数据
     *   - reason?: string - 解冻原因（可选）
     * 
     * @returns Promise<InventoryBatch> 更新后的批次信息
     * 
     * 权限要求：inventory:inventory:freeze
     * 
     * 代码示例：
     * ```typescript
     * const batch = await inventoryApi.unfreezeInventory(123, {
     *   reason: '检验合格，恢复正常'
     * })
     * ```
     */
    unfreezeInventory(id: number, data: InventoryUnfreezeForm) {
      return client.post<InventoryBatch>(API_ROUTES.inventoryBatches.unfreeze(id), data)
    },

    /**
     * 强制删除批次库存（级联软删除）
     * 
     * 使用场景：
     * - 录入完全错误的批次，需要彻底删除
     * - 测试数据清理
     * 
     * ⚠️ 危险操作：
     * - 会同时软删除批次和所有关联的事务记录
     * - 需要特殊权限：inventory:inventory:delete-force
     * - 建议仅管理员使用
     * 
     * @param id - 批次ID
     * @param data - 删除数据
     *   - reason: string - 删除原因（必填）
     * 
     * @returns Promise<void>
     * 
     * 业务规则：
     * - 有锁定数量的批次无法删除
     * - 所有删除操作都会记录审计日志
     * - 软删除后可通过数据库恢复
     * 
     * 代码示例：
     * ```typescript
     * await inventoryApi.forceDeleteInventory(123, {
     *   reason: '测试数据，需要删除'
     * })
     * ```
     */
    forceDeleteInventory(id: number, data: InventoryForceDeleteForm) {
      return client.delete<void>(API_ROUTES.inventoryBatches.forceDelete(id), { data })
    },

    // ==================== 领料/借用统计 API ====================

    /**
     * 获取领料记录列表
     * 
     * 使用场景：
     * - 查询所有带领料人的出库事务记录
     * - 支持按领料人、物料、时间范围筛选
     * 
     * @param params - 查询参数
     *   - receiver_id?: number - 领料人ID
     *   - material_id?: number - 物料ID
     *   - warehouse_id?: number - 仓库ID
     *   - business_type?: string - 业务类型
     *   - start_date?: string - 开始日期
     *   - end_date?: string - 结束日期
     *   - year_month?: string - 年月（格式：YYYY-MM）
     *   - search?: string - 搜索关键词
     *   - per_page?: number - 每页数量
     * 
     * @returns Promise<PaginationResult<InventoryTransaction>>
     */
    getPickingRecords(params?: Record<string, unknown>) {
      return client.get<PaginationResult<InventoryTransaction>>(
        API_ROUTES.pickingStatistic.list,
        { params }
      )
    },

    /**
     * 按人员统计领料
     * 
     * 使用场景：
     * - 查看每个人领用了多少物料
     * - 分析各员工的物料消耗情况
     * 
     * @param params - 查询参数
     *   - receiver_id?: number - 指定领料人
     *   - start_date?: string - 开始日期
     *   - end_date?: string - 结束日期
     *   - year_month?: string - 年月
     * 
     * @returns Promise<PickingStatsByPerson[]>
     */
    getPickingStatsByPerson(params?: Record<string, unknown>) {
      return client.get<PickingStatsByPerson[]>(
        API_ROUTES.pickingStatistic.pickingByPerson,
        { params }
      )
    },

    /**
     * 按物料统计领料
     * 
     * 使用场景：
     * - 查看每种物料被哪些人领用
     * - 分析物料消耗热度
     * 
     * @param params - 查询参数
     *   - material_id?: number - 指定物料
     *   - start_date?: string - 开始日期
     *   - end_date?: string - 结束日期
     *   - year_month?: string - 年月
     * 
     * @returns Promise<PickingStatsByMaterial[]>
     */
    getPickingStatsByMaterial(params?: Record<string, unknown>) {
      return client.get<PickingStatsByMaterial[]>(
        API_ROUTES.pickingStatistic.pickingByMaterial,
        { params }
      )
    },

    /**
     * 获取领料趋势
     * 
     * 使用场景：
     * - 分析领料数量的月度变化趋势
     * - 生成领料趋势图表
     * 
     * @param params - 查询参数
     *   - receiver_id?: number - 指定领料人
     *   - material_id?: number - 指定物料
     *   - start_date?: string - 开始日期
     *   - end_date?: string - 结束日期
     *   - detail_by_material?: boolean - 是否按物料细分
     * 
     * @returns Promise<PickingTrend>
     */
    getPickingTrend(params?: Record<string, unknown>) {
      return client.get<PickingTrend>(
        API_ROUTES.pickingStatistic.trend,
        { params }
      )
    },

    /**
     * 按人员统计借用
     * 
     * 使用场景：
     * - 查看每个人借用了多少物料
     * - 跟踪未归还物料
     * 
     * @param params - 查询参数
     *   - borrower_id?: number - 指定借用人
     *   - start_date?: string - 开始日期
     *   - end_date?: string - 结束日期
     *   - year_month?: string - 年月
     * 
     * @returns Promise<BorrowStatsByPerson[]>
     */
    getBorrowStatsByPerson(params?: Record<string, unknown>) {
      return client.get<BorrowStatsByPerson[]>(
        API_ROUTES.pickingStatistic.borrowByPerson,
        { params }
      )
    },

    /**
     * 按物料统计借用
     * 
     * 使用场景：
     * - 查看每种物料被哪些人借用
     * - 分析物料借用热度
     * 
     * @param params - 查询参数
     *   - material_id?: number - 指定物料
     *   - start_date?: string - 开始日期
     *   - end_date?: string - 结束日期
     *   - year_month?: string - 年月
     * 
     * @returns Promise<BorrowStatsByMaterial[]>
     */
    getBorrowStatsByMaterial(params?: Record<string, unknown>) {
      return client.get<BorrowStatsByMaterial[]>(
        API_ROUTES.pickingStatistic.borrowByMaterial,
        { params }
      )
    },

    /**
     * 获取借用趋势
     * 
     * 使用场景：
     * - 分析借用数量的月度变化趋势
     * - 生成借用趋势图表
     * 
     * @param params - 查询参数
     *   - borrower_id?: number - 指定借用人
     *   - material_id?: number - 指定物料
     *   - start_date?: string - 开始日期
     *   - end_date?: string - 结束日期
     * 
     * @returns Promise<BorrowTrend>
     */
    getBorrowTrend(params?: Record<string, unknown>) {
      return client.get<BorrowTrend>(
        API_ROUTES.pickingStatistic.borrowTrend,
        { params }
      )
    },
  }
}
